import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || (
  typeof window !== 'undefined' && window.location.hostname !== 'localhost'
    ? `${window.location.protocol}//${window.location.hostname.replace('almaysarah-dashboard', 'almaysarah-backend').replace('dashboard', 'backend')}/api`
    : 'http://localhost:3000/api'
)

export interface SuperuserLoginCredentials {
  email: string
  password: string
  mfaToken?: string
}

export interface SuperuserUser {
  id: string
  email: string
  firstName: string
  lastName: string
  isActive: boolean
  mfaEnabled: boolean
  lastLoginAt?: Date
  createdAt: Date
  updatedAt: Date
}

export interface SuperuserSession {
  superuser: SuperuserUser
  accessToken: string
  refreshToken: string
  permissions: string[]
  expiresAt: Date
}

export interface SuperuserAuthResponse {
  success: boolean
  data?: SuperuserSession
  error?: string
  requiresMfa?: boolean
}

export interface MfaSetupResponse {
  success: boolean
  data?: {
    secret: string
    qrCode: string
  }
  error?: string
}

export interface PasswordChangeRequest {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

class SuperuserAuthService {
  private session: SuperuserSession | null = null
  private sessionCheckInterval: NodeJS.Timeout | null = null

  constructor() {
    this.loadStoredSession()
    this.startSessionMonitoring()
  }

  private loadStoredSession() {
    const storedSession = localStorage.getItem('superuserSession')
    if (storedSession) {
      try {
        const session = JSON.parse(storedSession)
        // Check if session is still valid
        if (new Date(session.expiresAt) > new Date()) {
          this.session = session
        } else {
          this.clearStoredSession()
        }
      } catch (error) {
        console.error('Error parsing stored superuser session:', error)
        this.clearStoredSession()
      }
    }
  }

  private storeSession(session: SuperuserSession) {
    this.session = session
    localStorage.setItem('superuserSession', JSON.stringify(session))
  }

  private clearStoredSession() {
    this.session = null
    localStorage.removeItem('superuserSession')
  }

  private startSessionMonitoring() {
    // Check session validity every 5 minutes
    this.sessionCheckInterval = setInterval(() => {
      if (this.session && new Date(this.session.expiresAt) <= new Date()) {
        this.handleSessionExpiry()
      }
    }, 5 * 60 * 1000)
  }

  private handleSessionExpiry() {
    this.clearStoredSession()
    // Redirect to login or show session expired message
    window.location.href = '/platform/auth/login'
  }

  async login(credentials: SuperuserLoginCredentials): Promise<SuperuserAuthResponse> {
    try {
      const response = await axios.post(`${API_BASE_URL}/platform/auth/login`, {
        email: credentials.email,
        password: credentials.password,
        mfaToken: credentials.mfaToken
      })

      if (response.data.success) {
        const session: SuperuserSession = response.data.data
        this.storeSession(session)
        return {
          success: true,
          data: session
        }
      } else {
        return {
          success: false,
          error: response.data.error,
          requiresMfa: response.data.requiresMfa
        }
      }
    } catch (error: any) {
      console.error('Superuser login error:', error)

      // Check for MFA requirement (either from status 403 or specific error code)
      // The backend returns 403 for MFA_REQUIRED
      if (error.response?.status === 403 && (
        error.response?.data?.code === 'MFA_REQUIRED' ||
        error.response?.data?.error?.includes('MFA token is required')
      )) {
        return {
          success: false,
          requiresMfa: true,
          error: 'MFA token required'
        }
      }

      return {
        success: false,
        error: error.response?.data?.error || 'Login failed. Please try again.'
      }
    }
  }

  async setupMfa(): Promise<MfaSetupResponse> {
    if (!this.session) {
      return { success: false, error: 'Not authenticated' }
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/platform/auth/mfa/setup`,
        {},
        {
          headers: {
            Authorization: `Bearer ${this.session.accessToken}`
          }
        }
      )

      return response.data
    } catch (error: any) {
      console.error('MFA setup error:', error)
      return {
        success: false,
        error: error.response?.data?.error || 'MFA setup failed'
      }
    }
  }

  async verifyMfa(token: string): Promise<{ success: boolean; error?: string }> {
    if (!this.session) {
      return { success: false, error: 'Not authenticated' }
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/platform/auth/mfa/verify`,
        { token },
        {
          headers: {
            Authorization: `Bearer ${this.session.accessToken}`
          }
        }
      )

      return response.data
    } catch (error: any) {
      console.error('MFA verification error:', error)
      return {
        success: false,
        error: error.response?.data?.error || 'MFA verification failed'
      }
    }
  }

  async changePassword(passwordData: PasswordChangeRequest): Promise<{ success: boolean; error?: string }> {
    if (!this.session) {
      return { success: false, error: 'Not authenticated' }
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/platform/auth/change-password`,
        passwordData,
        {
          headers: {
            Authorization: `Bearer ${this.session.accessToken}`
          }
        }
      )

      return response.data
    } catch (error: any) {
      console.error('Password change error:', error)
      return {
        success: false,
        error: error.response?.data?.error || 'Password change failed'
      }
    }
  }

  async refreshToken(): Promise<boolean> {
    if (!this.session?.refreshToken) {
      return false
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/platform/auth/refresh`, {
        refreshToken: this.session.refreshToken
      })

      if (response.data.success) {
        const newSession: SuperuserSession = response.data.data
        this.storeSession(newSession)
        return true
      } else {
        this.clearStoredSession()
        return false
      }
    } catch (error) {
      console.error('Token refresh error:', error)
      this.clearStoredSession()
      return false
    }
  }

  async logout(): Promise<void> {
    try {
      if (this.session) {
        await axios.post(
          `${API_BASE_URL}/platform/auth/logout`,
          {},
          {
            headers: {
              Authorization: `Bearer ${this.session.accessToken}`
            }
          }
        )
      }
    } catch (error: any) {
      // Ignore 401 errors during logout - session is already invalid
      if (error.response?.status !== 401) {
        console.error('Logout error:', error)
      }
    } finally {
      this.clearStoredSession()
      if (this.sessionCheckInterval) {
        clearInterval(this.sessionCheckInterval)
      }
    }
  }

  getSession(): SuperuserSession | null {
    return this.session
  }

  getUser(): SuperuserUser | null {
    return this.session?.superuser || null
  }

  isAuthenticated(): boolean {
    return !!this.session && new Date(this.session.expiresAt) > new Date()
  }

  hasPermission(permission: string): boolean {
    return this.session?.permissions?.includes(permission) || false
  }

  getToken(): string | null {
    return this.session?.accessToken || null
  }
}

export const superuserAuthService = new SuperuserAuthService()
export default superuserAuthService